import { useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import GelirGider from "./pages/GelirGider";
import Satislar from "./pages/Satislar";
import CariHesaplar from "./pages/CariHesaplar";
import Tahsilatlar from "./pages/Tahsilatlar";
import Raporlar from "./pages/Raporlar";
import Profil from "./pages/Profil";
import OdemeYonetimi from "./pages/OdemeYonetimi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Check localStorage for existing session
    const saved = localStorage.getItem("finanskontrol_user");
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem("finanskontrol_user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("finanskontrol_user");
  };

  const checkAuth = () => {
    // Already handled by initial state
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, login, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gelir-gider"
        element={
          <ProtectedRoute>
            <GelirGider />
          </ProtectedRoute>
        }
      />
      <Route
        path="/satislar"
        element={
          <ProtectedRoute>
            <Satislar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cari-hesaplar"
        element={
          <ProtectedRoute>
            <CariHesaplar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tahsilatlar"
        element={
          <ProtectedRoute>
            <Tahsilatlar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/raporlar"
        element={
          <ProtectedRoute>
            <Raporlar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/odeme-yonetimi"
        element={
          <ProtectedRoute>
            <OdemeYonetimi />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profil"
        element={
          <ProtectedRoute>
            <Profil />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
